import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InvoiceApi } from '@app/services/invoice-api';
import {
  InvoiceInterface,
  CurrencyCode,
  PaymentMethodCode,
  PensionFundOptions,
} from '@app/interface/invoice';
import { HttpEventType } from '@angular/common/http';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmInvoiceUploadXmlComponent } from '@app/confirm-invoice-upload-xml/confirm-invoice-upload-xml.component';
import { MatDialog } from '@angular/material/dialog';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'izy-upload-xml',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    BackHeaderComponent,
  ],
  templateUrl: './upload-xml.component.html',
  styleUrl: './upload-xml.component.scss',
})
export class UploadXmlComponent implements OnInit {
  @ViewChild('fileInput') fileInputVariable!: ElementRef;

  uploadForm: FormGroup;
  selectedFile: File | null = null;
  isUploading = false;
  uploadProgress = 0;
  uploadError: string | null = null;
  uploadSuccess = false;
  xmlContent: string | null = null; // To store the XML content

  // uploaded invoice fields
  invoiceNumber: string = '';
  invoiceCompany: string = '';
  invoiceTotal: string = '';
  invoiceDate: string = '';

  isDragOver = false;

  constructor(
    private fb: FormBuilder,
    private invoiceApi: InvoiceApi,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {
    this.uploadForm = this.fb.group({
      organization_id: ['', [Validators.required, Validators.min(1)]],
    });
  }

  ngOnInit() {
    const organizationId = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.uploadForm.patchValue({
      organization_id: organizationId,
    });
  }

  // prepareNewUpload(): void {
  //   this.uploadSuccess = false;
  //   this.uploadError = null;
  //   this.uploadProgress = 0;
  //   this.isUploading = false;
  //   this.selectedFile = null;
  //   this.xmlContent = null;

  //   // Reset the Reactive Form but keep the organizationId
  //   const organizationId = this.uploadForm.get('organization_id')?.value;
  //   this.uploadForm.reset({ organization_id: organizationId });

  //   // Clear the native file input value
  //   if (this.fileInputVariable) {
  //     this.fileInputVariable.nativeElement.value = '';
  //   }
  // }

  formatToItalian(value: string | number): string {
    if (value === null || value === undefined) return '';

    return value.toString().replace(/\./g, ',');
  }

  formatDateToItalian(isoDate: string): string {
    // Handles both YYYY-MM-DD and YYYY-MM-DDTHH:mm:ss
    const datePart = isoDate.split('T')[0];
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return isoDate;
    return `${day}/${month}/${year}`;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.handleFileSelection(file);
    }
  }

  removeFile(event: Event): void {
    event.stopPropagation();
    this.selectedFile = null;
    this.xmlContent = null;
    this.uploadError = null;

    // Reset the file input
    const fileInput = document.getElementById('xmlFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // Update the existing onFileSelected method to use the common handler
  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.handleFileSelection(file);
    }
  }

  private handleFileSelection(file: File): void {
    if ((file && file.type === 'text/xml') || file.name.endsWith('.xml')) {
      this.selectedFile = file;
      this.uploadError = null;
      this.xmlContent = null;

      // console.log('File selected:', this.selectedFile);
      this.readXmlFile(file);
    } else {
      this.uploadError = 'Per favore seleziona un file XML valido';
      this.selectedFile = null;
      this.xmlContent = null;
    }
  }

  private readXmlFile(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        this.xmlContent = e.target.result as string;

        // console.log('=== XML FILE CONTENT ===');
        // console.log('File name:', file.name);
        // console.log('File size:', file.size, 'bytes');
        // console.log('File type:', file.type);
        // console.log('Full XML content:');
        // console.log(this.xmlContent);
        // console.log('=== END XML CONTENT ===');

        // Parse and extract specific data if needed
        if (this.xmlContent) {
          this.parseXmlContent(this.xmlContent);
        }
      } catch (error) {
        console.error('Error reading XML file:', error);
        this.uploadError = 'Error reading XML file';
      }
    };

    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      this.uploadError = 'Error reading file';
    };

    // Read as text
    reader.readAsText(file);
  }

  private parseXmlContent(xmlContent: string): void {
    try {
      // Create a DOM parser
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName('parsererror')[0];
      if (parseError) {
        console.error('XML parsing error:', parseError.textContent);
        return;
      }

      // Try to get root element name
      const rootElement = xmlDoc.documentElement;
      // console.log('Root element:', rootElement.nodeName);
      // console.log('XML version:', rootElement.getAttribute('versione'));

      // Try to extract some common Fattura Elettronica elements
      const numeroElement = xmlDoc.querySelector('Numero');
      if (numeroElement) {
        // console.log('Invoice number:', numeroElement.textContent);
        this.invoiceNumber = String(numeroElement.textContent);
      }

      const denominazioneCliente = xmlDoc.querySelector(
        'CessionarioCommittente Denominazione',
      );
      if (denominazioneCliente) {
        // console.log('Invoice date:', denominazioneCliente.textContent);
        this.invoiceCompany = String(denominazioneCliente.textContent);
      }

      const importoTotale = xmlDoc.querySelector('ImportoTotaleDocumento');
      if (importoTotale) {
        // console.log('Total amount:', importoTotale.textContent);
        this.invoiceTotal = String(importoTotale.textContent);
      }

      const dataEmissione = xmlDoc.querySelector('DatiGeneraliDocumento Data');
      if (dataEmissione?.textContent) {
        this.invoiceDate = this.formatDateToItalian(dataEmissione.textContent);
      }

      this.cdr.detectChanges();

      // Log all top-level elements for inspection
      // const topLevelElements = Array.from(rootElement.children);
      // console.log(
      //   'Top level elements:',
      //   topLevelElements.map((el) => el.nodeName),
      // );
    } catch (error) {
      console.error('Error parsing XML:', error);

      this.cdr.detectChanges();
    }
  }

  // Optional: Method to display XML content in template for debugging
  getXmlPreview(): string {
    if (!this.xmlContent) return '';

    // Return first 200 characters for preview
    return this.xmlContent.length > 200
      ? this.xmlContent.substring(0, 200) + '...'
      : this.xmlContent;
  }

  onSubmit(): void {
    if (this.uploadForm.valid && this.selectedFile) {
      this.uploadInvoice();
    }
  }

  private async uploadInvoice(): Promise<void> {
    // akss the user for confirmation
    const dialogRef = this.dialog.open(ConfirmInvoiceUploadXmlComponent, {
      width: '90vw',
      maxWidth: '400px',
      height: '500px',
      panelClass: 'izy-confirm-dialog-panel',
    });
    const result = await dialogRef.afterClosed().toPromise();

    if (!result) {
      // User cancelled the dialog
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadError = null;
    this.uploadSuccess = false;

    const organizationId = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.invoiceApi
      .uploadInvoiceXml(organizationId, this.selectedFile!)
      .subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadProgress = Math.round(
              (100 * event.loaded) / event.total,
            );
          } else if (event.type === HttpEventType.Response) {
            // 1. Stop the loading state
            this.isUploading = false;
            this.uploadProgress = 0;

            // 2. Trigger the success UI
            this.uploadSuccess = true;

            // 3. Clean up the "input" data so the UI looks ready for the next one,
            // but DO NOT reset the uploadSuccess flag here.
            this.selectedFile = null;
            this.xmlContent = null;

            const fileInput = document.getElementById(
              'xmlFile',
            ) as HTMLInputElement;
            if (fileInput) fileInput.value = '';

            // 4. Keep the form valid for the NEXT upload by patching the ID back in
            const orgId = this.uploadForm.get('organization_id')?.value;
            this.uploadForm.reset({ organization_id: orgId });

            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          this.isUploading = false;
          this.uploadProgress = 0;
          this.uploadError = error.error?.message || "Errore durante l'invio.";

          this.cdr.detectChanges();
        },
      });
  }

  private resetForm(): void {
    // 1. Capture the organization ID BEFORE resetting anything
    const organizationId = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    // 2. Clear file and UI state
    this.selectedFile = null;
    this.xmlContent = null;
    this.uploadProgress = 0;
    this.uploadError = null;
    // Note: We don't set uploadSuccess = false here yet
    // so the user can see the success message briefly.

    // 3. Reset the Reactive Form with the ID preserved
    this.uploadForm.reset({
      organization_id: organizationId,
    });

    // 4. Clear the actual HTML input value so the same file can be re-selected
    const fileInput = document.getElementById('xmlFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  prepareNewUpload(): void {
    // Clear the success message so the "Importa" button and drop-zone are useful again
    this.uploadSuccess = false;
    this.uploadError = null;

    // Ensure the form is ready
    const organizationId = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );
    this.uploadForm.patchValue({ organization_id: organizationId });
  }

  navigateToInvoices() {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    // console.log(org_id);
    this.router.navigate([`/${org_id}/invoices_table`]);
  }

  backToDashboard(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/dashboard`]);
  }
}
